import { zodResolver } from '@hookform/resolvers/zod'
import type { Resolver } from 'react-hook-form'
import { z } from 'zod'

import { legacyCronMap } from '@/constants'

import {
  DEFAULT_EXPIRATION_HOURS,
  STOCK_RULE_DEFAULTS,
} from '../../lib/stockImportsDefaults'
import {
  configFormSchema,
  configSourceRulesSchema,
} from '../../lib/stockImportsValidation'
import type {
  ImportFileFormat,
  ImportSourceType,
  ProductSupplierDto,
  SheetSelectionType,
  StockImportConfigModel,
} from '../../types/stockImports.types'
import type { ExcelMapperValues } from '../UrlExcelMapper/UrlExcelMapper.types'

export const SELF_SUPPLIER_FORM_VALUE = '__SELF_PRODUCT_SUPPLIER__'
export const DEFAULT_CRON_EXPRESSION = '0 0 0 * * *'
const DEFAULT_IMAP_PORT = '993'

export const CONFIG_NUMBER_FIELDS = [
  'expirationHours',
  'startRowIndex',
  'endRowIndex',
  'articleColumnNumber',
  'articleInchColumnNumber',
  'barcodeColumnNumber',
  'descriptionColumnNumber',
  'brandColumnNumber',
  'quantityColumnNumber',
  'maxAllowedMissingPercentage',
  'missingPercentageSpikeAlert',
  'minLoadedPercentOfMedian',
  'maxInvalidQuantityPercentage',
  'stalledAlertBusinessDays',
] as const

type ConfigNumberField = (typeof CONFIG_NUMBER_FIELDS)[number]

type ConfigTextField =
  | 'supplierId'
  | 'storeId'
  | 'cronExpression'
  | 'sheetNames'
  | 'sheetIndexes'
  | 'url'
  | 'expectedSender'
  | 'subjectMask'
  | 'fileMask'
  | 'imapHost'
  | 'imapPort'
  | 'imapLogin'
  | 'imapPasswordEncrypted'
  | 'fallbackBrandId'
  | 'expectedQuantityHeader'

export type TextQuantityMappingRow = { key: string; value: string }

export type ConfigFormInput = Record<
  ConfigNumberField | ConfigTextField,
  string
> & {
  sourceType: ImportSourceType | ''
  fileFormat: ImportFileFormat | ''
  sheetSelectionType: SheetSelectionType
  excludeWeekends: boolean
  useSsl: boolean
  detectBrandInDescription: boolean
  allowedBrands: string[]
  ignoredBrands: string[]
  textQuantityMapping: TextQuantityMappingRow[]
}

export const CONFIG_FORM_FIELDS: ReadonlyArray<keyof ConfigFormInput> = [
  ...CONFIG_NUMBER_FIELDS,
  'supplierId',
  'storeId',
  'cronExpression',
  'sheetNames',
  'sheetIndexes',
  'url',
  'expectedSender',
  'subjectMask',
  'fileMask',
  'imapHost',
  'imapPort',
  'imapLogin',
  'imapPasswordEncrypted',
  'fallbackBrandId',
  'expectedQuantityHeader',
  'sourceType',
  'fileFormat',
  'sheetSelectionType',
  'excludeWeekends',
  'useSsl',
  'detectBrandInDescription',
  'allowedBrands',
  'ignoredBrands',
  'textQuantityMapping',
]

const toNumberOrUndefined = (value: unknown) => {
  if (typeof value !== 'string') return value
  return value.trim() === '' ? undefined : Number(value)
}

const toNumberOrNull = (value: unknown) => {
  if (typeof value !== 'string') return value
  return value.trim() === '' ? null : Number(value)
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export const toConfigSchemaInput = (value: unknown) => {
  if (!isRecord(value)) return value
  const record: Record<string, unknown> = { ...value }
  CONFIG_NUMBER_FIELDS.forEach((field) => {
    record[field] = toNumberOrUndefined(record[field])
  })
  if (Array.isArray(record.textQuantityMapping)) {
    record.textQuantityMapping = record.textQuantityMapping.map((row) =>
      isRecord(row) ? { ...row, value: toNumberOrNull(row.value) } : row
    )
  }
  return record
}

export const configFormResolverSchema = z.preprocess(
  toConfigSchemaInput,
  configFormSchema
)

export type ConfigFormValues = z.output<typeof configFormSchema>

export type ConfigSourceType = ConfigFormInput['sourceType']
export type ConfigFileFormat = ConfigFormInput['fileFormat']

const resolveWholeForm = zodResolver(configFormResolverSchema)
const resolveSourceRules = zodResolver(configSourceRulesSchema)

export const configFormResolver: Resolver<ConfigFormInput> = async (
  values,
  context,
  options
) => {
  const result = await resolveWholeForm(values, context, options)
  if (Object.keys(result.errors).length === 0) return result
  const sourceResult = await resolveSourceRules(values, context, options)
  return { values: {}, errors: { ...sourceResult.errors, ...result.errors } }
}

export const normalizeCronExpression = (value?: string | null) => {
  if (!value) return undefined
  return legacyCronMap[value] ?? value
}

const toUserNumber = (index?: number | null) =>
  index === undefined || index === null ? '' : String(index + 1)

const toInputNumber = (value?: number | null) =>
  value === undefined || value === null ? '' : String(value)

export const SECOND_STEP_RESET_VALUES: Partial<ConfigFormInput> = {
  startRowIndex: '',
  endRowIndex: '',
  sheetSelectionType: 'ALL',
  sheetNames: '',
  sheetIndexes: '',
  articleColumnNumber: '',
  articleInchColumnNumber: '',
  barcodeColumnNumber: '',
  descriptionColumnNumber: '',
  brandColumnNumber: '',
  quantityColumnNumber: '',
  allowedBrands: [],
  ignoredBrands: [],
  fallbackBrandId: '',
  detectBrandInDescription: false,
  maxAllowedMissingPercentage: String(
    STOCK_RULE_DEFAULTS.maxAllowedMissingPercentage
  ),
  missingPercentageSpikeAlert: String(
    STOCK_RULE_DEFAULTS.missingPercentageSpikeAlert
  ),
  expectedQuantityHeader: '',
  textQuantityMapping: [],
}

export const hasSecondStepData = (values: ConfigFormInput) =>
  values.sheetSelectionType !== 'ALL' ||
  values.detectBrandInDescription ||
  values.allowedBrands.length > 0 ||
  values.ignoredBrands.length > 0 ||
  values.textQuantityMapping.length > 0 ||
  (
    [
      'startRowIndex',
      'endRowIndex',
      'sheetNames',
      'sheetIndexes',
      'articleColumnNumber',
      'articleInchColumnNumber',
      'barcodeColumnNumber',
      'descriptionColumnNumber',
      'brandColumnNumber',
      'quantityColumnNumber',
      'fallbackBrandId',
      'expectedQuantityHeader',
    ] as const
  ).some((field) => values[field].trim() !== '')

const readLegacyValue = (settings: object | undefined, key: string) => {
  if (!settings || !(key in settings)) return undefined
  const value: unknown = Reflect.get(settings, key)
  return value
}

const readLegacyString = (settings: object | undefined, key: string) => {
  const value = readLegacyValue(settings, key)
  return typeof value === 'string' ? value : undefined
}

const readLegacyBoolean = (settings: object | undefined, key: string) => {
  const value = readLegacyValue(settings, key)
  return typeof value === 'boolean' ? value : undefined
}

export const buildConfigFormDefaults = (
  config: StockImportConfigModel | null,
  supplier: ProductSupplierDto | null,
  isSelfSupplierMode: boolean
): ConfigFormInput => {
  const sourceSettings = config?.sourceSettings
  const imap = sourceSettings?.customImapSettings
  const sourceType = config?.sourceType ?? ''
  const stockRules = config?.stockRules
  const columns = config?.columnMapping
  const legacy = (key: string) => readLegacyString(sourceSettings, key)

  return {
    supplierId: isSelfSupplierMode
      ? SELF_SUPPLIER_FORM_VALUE
      : (config?.productSupplier?.id ?? supplier?.id ?? ''),
    storeId: config?.store?.id ?? '',
    sourceType,
    fileFormat: config?.fileFormat ?? '',
    cronExpression:
      sourceType === 'EMAIL'
        ? ''
        : (normalizeCronExpression(config?.cronExpression) ??
          DEFAULT_CRON_EXPRESSION),
    expirationHours: String(
      config?.expirationHours ?? DEFAULT_EXPIRATION_HOURS
    ),
    excludeWeekends: config?.excludeWeekends ?? true,
    startRowIndex: toUserNumber(sourceSettings?.startRowIndex),
    endRowIndex: toUserNumber(sourceSettings?.endRowIndex),
    sheetSelectionType:
      sourceSettings?.sheetSelectionRule?.selectionType ?? 'ALL',
    sheetNames:
      sourceSettings?.sheetSelectionRule?.sheetNames?.join(', ') ?? '',
    sheetIndexes:
      sourceSettings?.sheetSelectionRule?.sheetIndexes?.join(', ') ?? '',
    url: sourceSettings?.cloudUrlSettings?.url ?? legacy('url') ?? '',
    expectedSender:
      (sourceType === 'CUSTOM_IMAP'
        ? imap?.expectedSender
        : sourceSettings?.emailSettings?.expectedSender) ??
      legacy('expectedSender') ??
      '',
    subjectMask:
      (sourceType === 'CUSTOM_IMAP'
        ? imap?.subjectMask
        : sourceSettings?.emailSettings?.subjectMask) ??
      legacy('subjectMask') ??
      '',
    fileMask: sourceSettings?.fileMask ?? '',
    imapHost: imap?.imapHost ?? legacy('imapHost') ?? '',
    imapPort: imap?.imapPort ?? legacy('imapPort') ?? DEFAULT_IMAP_PORT,
    imapLogin: imap?.imapLogin ?? legacy('imapLogin') ?? '',
    imapPasswordEncrypted:
      imap?.imapPasswordEncrypted ?? legacy('imapPasswordEncrypted') ?? '',
    useSsl: imap?.useSsl ?? readLegacyBoolean(sourceSettings, 'useSsl') ?? true,
    articleColumnNumber: toUserNumber(columns?.articleColumnNumber),
    articleInchColumnNumber: toUserNumber(columns?.articleInchColumnNumber),
    barcodeColumnNumber: toUserNumber(columns?.barcodeColumnNumber),
    descriptionColumnNumber: toUserNumber(columns?.descriptionColumnNumber),
    brandColumnNumber: toUserNumber(columns?.brandColumnNumber),
    quantityColumnNumber: toUserNumber(columns?.quantityColumnNumber),
    allowedBrands: config?.filters.allowedBrands ?? [],
    ignoredBrands: config?.filters.ignoredBrands ?? [],
    fallbackBrandId: stockRules?.fallbackBrandId ?? '',
    detectBrandInDescription: stockRules?.detectBrandInDescription ?? false,
    maxAllowedMissingPercentage: toInputNumber(
      stockRules?.maxAllowedMissingPercentage ??
        STOCK_RULE_DEFAULTS.maxAllowedMissingPercentage
    ),
    missingPercentageSpikeAlert: toInputNumber(
      stockRules?.missingPercentageSpikeAlert ??
        STOCK_RULE_DEFAULTS.missingPercentageSpikeAlert
    ),
    minLoadedPercentOfMedian: toInputNumber(
      stockRules?.minLoadedPercentOfMedian ??
        STOCK_RULE_DEFAULTS.minLoadedPercentOfMedian
    ),
    maxInvalidQuantityPercentage: toInputNumber(
      stockRules?.maxInvalidQuantityPercentage ??
        STOCK_RULE_DEFAULTS.maxInvalidQuantityPercentage
    ),
    stalledAlertBusinessDays: toInputNumber(
      stockRules?.stalledAlertBusinessDays ??
        STOCK_RULE_DEFAULTS.stalledAlertBusinessDays
    ),
    expectedQuantityHeader: stockRules?.expectedQuantityHeader ?? '',
    textQuantityMapping: Object.entries(
      stockRules?.textQuantityMapping ?? {}
    ).map(([key, value]) => ({ key, value: String(value) })),
  }
}

const MAPPER_NUMBER_FIELDS = [
  'startRowIndex',
  'endRowIndex',
  'articleColumnNumber',
  'articleInchColumnNumber',
  'barcodeColumnNumber',
  'descriptionColumnNumber',
  'brandColumnNumber',
  'quantityColumnNumber',
] as const

export type MapperFormValues = Pick<
  ConfigFormInput,
  (typeof MAPPER_NUMBER_FIELDS)[number]
>

export const toMapperValues = (values: MapperFormValues): ExcelMapperValues =>
  Object.fromEntries(
    MAPPER_NUMBER_FIELDS.map((field) => [
      field,
      values[field].trim() === '' ? undefined : Number(values[field]),
    ])
  )

export const fromMapperValues = (
  values: ExcelMapperValues
): Partial<ConfigFormInput> => {
  const next: Partial<ConfigFormInput> = {}
  MAPPER_NUMBER_FIELDS.forEach((field) => {
    if (field in values) {
      const value = values[field]
      next[field] = value === undefined ? '' : String(value)
    }
  })
  if (values.sheetSelectionType)
    next.sheetSelectionType = values.sheetSelectionType
  if ('sheetNames' in values) next.sheetNames = values.sheetNames ?? ''
  if ('sheetIndexes' in values) next.sheetIndexes = values.sheetIndexes ?? ''
  return next
}
